import React, { useState, useEffect, useMemo } from 'react';
import { FiChevronLeft, FiChevronRight, FiCalendar, FiFilter, FiLock } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../../Api';
import { PageComponent, Card, Button, Select, Skeletons, MultiSelect } from '../../../components';
import DayExecutionsModal from './DayExecutionsModal';
import { usePermission } from '../../../hooks/usePermission';
import '../../CompanyManager/style.css';
import './style.css';

const STATUS_LABELS = {
  em_execucao: 'Em Execução',
  finalizado: 'Finalizado',
  cancelado: 'Cancelado',
  pre_agendada: 'Pré-agendada',
};

const SERVICE_TYPE_LABELS = {
  cortesia: 'Cortesia',
  terceirizado: 'Terceirizado',
  padrao: 'Padrão',
};

const REQUEST_TYPE_LABELS = {
  corretiva: 'Corretiva',
  preventiva: 'Preventiva',
};

const WEEKDAYS = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];

function getMonthDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days = [];

  // Fill previous month days
  const startWeekday = firstDay.getDay();
  for (let i = startWeekday - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, currentMonth: false });
  }

  // Current month days
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({ date: new Date(year, month, i), currentMonth: true });
  }

  // Fill next month days
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({ date: new Date(year, month + 1, i), currentMonth: false });
  }

  return days;
}

function formatDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export default function ServiceOrderSchedule() {
  const permissions = usePermission('service_orders');
  const [selectedCompany] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('selectedCompany'));
    } catch (e) {
      return null;
    }
  });

  const [companies, setCompanies] = useState([]);
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [executions, setExecutions] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [requestTypeFilter, setRequestTypeFilter] = useState('all');

  // Day modal
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDayExecs, setSelectedDayExecs] = useState([]);
  const [selectedDayScheduled, setSelectedDayScheduled] = useState([]);
  const [isDayLoading, setIsDayLoading] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthDays = useMemo(() => getMonthDays(year, month), [year, month]);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (permissions.canView) {
      loadCalendar();
    }
  }, [year, month, companyFilterValues, companies, permissions.canView]);

  async function loadCompanies() {
    try {
      const data = await Api.getCompanies();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadCalendar() {
    if (!selectedCompany?.id && (!companies || companies.length === 0)) return;
    setLoading(true);
    try {
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${lastDay}`;

      const allValue = 'all';
      const specificIds = companyFilterValues.filter(v => v !== allValue);
      const hasAllSelected = companyFilterValues.includes(allValue);

      const companyIds =
        hasAllSelected || specificIds.length === 0
          ? (companies.length > 0 ? companies.map(c => c.id) : [selectedCompany.id])
          : specificIds.map(id => Number(id));

      const allExecutions = [];
      const allScheduled = [];

      await Promise.all(companyIds.map(async (cId) => {
        const data = await Api.getServiceOrderCalendarSummary(cId, { startDate, endDate });
        if (Array.isArray(data.executions)) allExecutions.push(...data.executions);
        if (Array.isArray(data.scheduled)) allScheduled.push(...data.scheduled);
      }));

      setExecutions(allExecutions);
      setScheduled(allScheduled);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar calendário.');
    } finally {
      setLoading(false);
    }
  }

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      if (specificIds.length === companies.length && companies.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);
  }

  const executionsByDay = useMemo(() => {
    const map = {};
    const filtered = executions.filter(exec => {
      if (statusFilter !== 'all' && exec.status !== statusFilter) return false;
      const st = exec.serviceOrder?.service_type;
      const rt = exec.serviceOrder?.request_type;
      if (serviceTypeFilter !== 'all' && st !== serviceTypeFilter) return false;
      if (requestTypeFilter !== 'all' && rt !== requestTypeFilter) return false;
      return true;
    });
    filtered.forEach(exec => {
      const dateKey = exec.started_at ? formatDateKey(new Date(exec.started_at)) : null;
      if (dateKey) {
        if (!map[dateKey]) map[dateKey] = [];
        map[dateKey].push(exec);
      }
    });
    return map;
  }, [executions, statusFilter, serviceTypeFilter, requestTypeFilter]);

  const scheduledByDay = useMemo(() => {
    const map = {};
    const includeScheduled = statusFilter === 'all' || statusFilter === 'pre_agendada';
    const filtered = includeScheduled
      ? scheduled.filter(item => {
          const st = item.serviceOrder?.service_type;
          const rt = item.serviceOrder?.request_type;
          if (serviceTypeFilter !== 'all' && st !== serviceTypeFilter) return false;
          if (requestTypeFilter !== 'all' && rt !== requestTypeFilter) return false;
          return true;
        })
      : [];
    filtered.forEach(item => {
      const dateKey = item.date;
      if (dateKey) {
        if (!map[dateKey]) map[dateKey] = [];
        map[dateKey].push(item);
      }
    });
    return map;
  }, [scheduled, statusFilter, serviceTypeFilter, requestTypeFilter]);

  function prevMonth() {
    setCurrentDate(new Date(year, month - 1, 1));
  }

  function nextMonth() {
    setCurrentDate(new Date(year, month + 1, 1));
  }

  async function handleDayClick(dayObj) {
    const key = formatDateKey(dayObj.date);
    setSelectedDay(key);
    setSelectedDayExecs([]);
    setSelectedDayScheduled([]);
    setIsDayModalOpen(true);

    try {
      setIsDayLoading(true);
      const startDate = key;
      const endDate = key;

      const allValue = 'all';
      const specificIds = companyFilterValues.filter(v => v !== allValue);
      const hasAllSelected = companyFilterValues.includes(allValue);

      const companyIds =
        hasAllSelected || specificIds.length === 0
          ? (companies.length > 0 ? companies.map(c => c.id) : [selectedCompany.id])
          : specificIds.map(id => Number(id));

      const allExecutions = [];
      const allScheduled = [];

      await Promise.all(companyIds.map(async (cId) => {
        const data = await Api.getServiceOrderCalendar(cId, { startDate, endDate });
        if (Array.isArray(data.executions)) allExecutions.push(...data.executions);
        if (Array.isArray(data.scheduled)) allScheduled.push(...data.scheduled);
      }));

      let filteredExecutions = allExecutions;
      if (statusFilter !== 'all') {
        filteredExecutions = filteredExecutions.filter(exec => exec.status === statusFilter);
      }
      if (serviceTypeFilter !== 'all') {
        filteredExecutions = filteredExecutions.filter(exec => exec.serviceOrder?.service_type === serviceTypeFilter);
      }
      if (requestTypeFilter !== 'all') {
        filteredExecutions = filteredExecutions.filter(exec => exec.serviceOrder?.request_type === requestTypeFilter);
      }

      const includeScheduled = statusFilter === 'all' || statusFilter === 'pre_agendada';
      const filteredScheduled = includeScheduled
        ? allScheduled.filter(item => {
            const st = item.serviceOrder?.service_type;
            const rt = item.serviceOrder?.request_type;
            if (serviceTypeFilter !== 'all' && st !== serviceTypeFilter) return false;
            if (requestTypeFilter !== 'all' && rt !== requestTypeFilter) return false;
            return true;
          })
        : [];

      setSelectedDayExecs(filteredExecutions);
      setSelectedDayScheduled(filteredScheduled);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar detalhes do dia.');
    } finally {
      setIsDayLoading(false);
    }
  }

  const today = formatDateKey(new Date());

  const monthName = currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiLock size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        <div className="manager-header">
          <div className="manager-header-title">
            <div className="manager-header-icon">
              <FiCalendar size={22} />
            </div>
            <div>
              <h1>Agenda de Execuções</h1>
              <p>Visualize as execuções de ordens de serviço no calendário.</p>
            </div>
          </div>
        </div>

        <Card className="manager-filters-card" style={{ marginBottom: 16 }}>
          <div className="manager-filters-header">
            <div className="manager-filters-title">
              <FiFilter size={16} />
              <span>Filtros</span>
            </div>
          </div>
          <div
            className="manager-filters-grid"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}
          >
            <div className="manager-filter-group">
              <label className="manager-filter-label">Empresa</label>
              <MultiSelect
                id="filter-company-calendar"
                options={[
                  { value: 'all', label: 'TODAS' },
                  ...companies.map(c => ({
                    value: String(c.id),
                    label: c.corporate_name || c.trade_name || c.name || c.razao_social || c.nome_fantasia || `Empresa ${c.id}`,
                  })),
                ]}
                value={companyFilterValues}
                onChange={handleCompanyFilterChange}
                searchable
              />
            </div>
            <div className="manager-filter-group">
              <label className="manager-filter-label">Status</label>
              <Select
                id="filter-status-calendar"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                options={[
                  { value: 'all', label: 'Todos' },
                  { value: 'pre_agendada', label: STATUS_LABELS.pre_agendada },
                  { value: 'em_execucao', label: STATUS_LABELS.em_execucao },
                  { value: 'finalizado', label: STATUS_LABELS.finalizado },
                  { value: 'cancelado', label: STATUS_LABELS.cancelado },
                ]}
                placeholder="SELECIONE..."
              />
            </div>
            <div className="manager-filter-group">
              <label className="manager-filter-label">Tipo de Serviço</label>
              <Select
                id="filter-service-type-calendar"
                value={serviceTypeFilter}
                onChange={e => setServiceTypeFilter(e.target.value)}
                options={[
                  { value: 'all', label: 'Todos' },
                  { value: 'cortesia', label: SERVICE_TYPE_LABELS.cortesia },
                  { value: 'terceirizado', label: SERVICE_TYPE_LABELS.terceirizado },
                  { value: 'padrao', label: SERVICE_TYPE_LABELS.padrao },
                ]}
                placeholder="SELECIONE..."
              />
            </div>
            <div className="manager-filter-group">
              <label className="manager-filter-label">Tipo de Solicitação</label>
              <Select
                id="filter-request-type-calendar"
                value={requestTypeFilter}
                onChange={e => setRequestTypeFilter(e.target.value)}
                options={[
                  { value: 'all', label: 'Todos' },
                  { value: 'corretiva', label: REQUEST_TYPE_LABELS.corretiva },
                  { value: 'preventiva', label: REQUEST_TYPE_LABELS.preventiva },
                ]}
                placeholder="SELECIONE..."
              />
            </div>
          </div>
        </Card>

        <Card className="so-calendar-container">
          <div className="so-calendar-nav">
            <div className="so-calendar-nav-left">
              <Button type="button" className="so-calendar-nav-button" onClick={prevMonth}>
                <FiChevronLeft size={18} />
              </Button>
              <h2>{monthName}</h2>
              <Button type="button" className="so-calendar-nav-button" onClick={nextMonth}>
                <FiChevronRight size={18} />
              </Button>
            </div>
            <Button type="button" className="so-calendar-today-button" onClick={() => setCurrentDate(new Date())}>
              Hoje
            </Button>
          </div>

          <div className="so-calendar-legend">
            <div className="so-calendar-legend-item">
              <span className="so-calendar-legend-dot scheduled" />
              <span>{STATUS_LABELS.pre_agendada}</span>
            </div>
            <div className="so-calendar-legend-item">
              <span className="so-calendar-legend-dot em_execucao" />
              <span>{STATUS_LABELS.em_execucao}</span>
            </div>
            <div className="so-calendar-legend-item">
              <span className="so-calendar-legend-dot finalizado" />
              <span>{STATUS_LABELS.finalizado}</span>
            </div>
            <div className="so-calendar-legend-item">
              <span className="so-calendar-legend-dot cancelado" />
              <span>{STATUS_LABELS.cancelado}</span>
            </div>
          </div>

          <div className="so-calendar-grid">
            {WEEKDAYS.map(day => (
              <div key={day} className="so-calendar-header-cell">{day}</div>
            ))}

            {monthDays.map((dayObj, idx) => {
              const key = formatDateKey(dayObj.date);
              const dayExecs = executionsByDay[key] || [];
              const dayScheduled = scheduledByDay[key] || [];
              const isToday = key === today;

              const scheduledCount = dayScheduled.length;
              const runningCount = dayExecs.filter(e => e.status === 'em_execucao').length;
              const finishedCount = dayExecs.filter(e => e.status === 'finalizado').length;
              const cancelledCount = dayExecs.filter(e => e.status === 'cancelado').length;
              const hasAnyStatus = scheduledCount || runningCount || finishedCount || cancelledCount;

              return (
                <div
                  key={idx}
                  className={`so-calendar-cell ${!dayObj.currentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''}`}
                  onClick={() => handleDayClick(dayObj)}
                >
                  <div className="so-calendar-day-number">{dayObj.date.getDate()}</div>
                  {hasAnyStatus && (
                    <div className="so-calendar-status-badges">
                      {scheduledCount > 0 && (
                        <div className="so-calendar-status-badge pre">
                          <span className="so-calendar-status-left">
                            <span className="so-calendar-status-dot scheduled" />
                            <span>{STATUS_LABELS.pre_agendada}</span>
                          </span>
                          <span className="so-calendar-status-count">{scheduledCount}</span>
                        </div>
                      )}
                      {runningCount > 0 && (
                        <div className="so-calendar-status-badge running">
                          <span className="so-calendar-status-left">
                            <span className="so-calendar-status-dot em_execucao" />
                            <span>{STATUS_LABELS.em_execucao}</span>
                          </span>
                          <span className="so-calendar-status-count">{runningCount}</span>
                        </div>
                      )}
                      {finishedCount > 0 && (
                        <div className="so-calendar-status-badge done">
                          <span className="so-calendar-status-left">
                            <span className="so-calendar-status-dot finalizado" />
                            <span>{STATUS_LABELS.finalizado}</span>
                          </span>
                          <span className="so-calendar-status-count">{finishedCount}</span>
                        </div>
                      )}
                      {cancelledCount > 0 && (
                        <div className="so-calendar-status-badge cancelled">
                          <span className="so-calendar-status-left">
                            <span className="so-calendar-status-dot cancelado" />
                            <span>{STATUS_LABELS.cancelado}</span>
                          </span>
                          <span className="so-calendar-status-count">{cancelledCount}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <DayExecutionsModal
        isOpen={isDayModalOpen}
        onClose={() => setIsDayModalOpen(false)}
        date={selectedDay}
        executions={selectedDayExecs}
        scheduled={selectedDayScheduled}
        isLoading={isDayLoading}
      />
    </PageComponent>
  );
}
